import React from 'react';
import { Row, Col } from 'antd';
import { I18n } from '@aws-amplify/core';
import { WarningOutlined } from '@ant-design/icons';

import { WORK_CONDITION_OPTIONS } from '@/components/views/Report/KimLhc/Constants';
import { getWarningIconColor } from '../../../../utils/helper';
import { KimLhcCategoryKey, type WorkConditionCategory } from '@/types';

import * as S from '@/components/views/Report/KimLhc/Report/Steps/Result/styles';

interface WorkConditionCategoryProps {
	data: WorkConditionCategory;
}

interface ActiveCondition {
	key: string;
	titleKey?: string;
	resultTitleKey?: string;
	descriptionKey?: string;
	labelKey?: string;
}

function getActiveConditions(data: WorkConditionCategory): ActiveCondition[] {
	const conditions: ActiveCondition[] = [];

	if (data.handsPosition) {
		const option = WORK_CONDITION_OPTIONS.hands_position.find((o) => o.value === data.handsPosition);
		if (option) {
			conditions.push({
				key: option.value,
				resultTitleKey: option.resultTitleKey,
				labelKey: option.labelKey,
				descriptionKey: option.descriptionKey
			});
		}
	}

	if (data.transfer) {
		const option = WORK_CONDITION_OPTIONS.transfer.find((o) => o.value === data.transfer);
		if (option) {
			conditions.push({
				key: option.value,
				titleKey: option.titleKey,
				resultTitleKey: option.resultTitleKey,
				descriptionKey: option.descriptionKey
			});
		}
	}

	if (data.spatialCondition) {
		const option = WORK_CONDITION_OPTIONS.spatial_condition.find((o) => o.value === data.spatialCondition);
		if (option) {
			conditions.push({
				key: option.value,
				titleKey: option.titleKey,
				resultTitleKey: option.resultTitleKey,
				descriptionKey: option.descriptionKey
			});
		}
	}

	if (data.adverse) {
		const option = WORK_CONDITION_OPTIONS.adverse_conditions.find((o) => o.value === data.adverse);
		if (option) {
			conditions.push({
				key: option.value,
				resultTitleKey: option.resultTitleKey,
				labelKey: option.labelKey,
				descriptionKey: option.descriptionKey
			});
		}
	}

	if (data.clothes) {
		const option = WORK_CONDITION_OPTIONS.clothes.find((o) => o.value === data.clothes);
		if (option) {
			conditions.push({
				key: option.value,
				resultTitleKey: option.resultTitleKey,
				labelKey: option.labelKey,
				descriptionKey: option.descriptionKey
			});
		}
	}

	if (data.holdLoad) {
		const option = WORK_CONDITION_OPTIONS.hold_load.find((o) => o.value === data.holdLoad);
		if (option) {
			conditions.push({
				key: option.value,
				titleKey: option.titleKey,
				resultTitleKey: option.resultTitleKey,
				descriptionKey: option.descriptionKey
			});
		}
	}

	return conditions;
}

function WorkConditionLabel({ condition }: Readonly<{ condition: ActiveCondition }>) {
	if (condition.resultTitleKey && condition.descriptionKey) {
		const titleKeyToDisplay = condition.resultTitleKey ?? condition.titleKey;

		return (
			<Row>
				<Col span={24}>
					<S.FactorDescriptionHeader>{I18n.get(titleKeyToDisplay)}: </S.FactorDescriptionHeader>
					<S.FactorDescriptionText>{I18n.get(condition.descriptionKey)}</S.FactorDescriptionText>
				</Col>
			</Row>
		);
	}

	if (condition.labelKey) {
		return I18n.get(condition.labelKey);
	}

	return null;
}

export function WorkConditionCategory({ data }: Readonly<WorkConditionCategoryProps>): JSX.Element {
	const activeConditions = getActiveConditions(data);

	if (activeConditions.length === 0) {
		return (
			<S.AccordionContent>
				<S.EmptyText>{I18n.get('No unfavorable conditions reported')}</S.EmptyText>
			</S.AccordionContent>
		);
	}

	return (
		<S.AccordionContent>
			<S.FactorsList>
				{activeConditions.map((condition) => (
					<S.FactorItem key={condition.key}>
						<S.WarningIcon>
							<WarningOutlined style={{ color: getWarningIconColor(KimLhcCategoryKey.WORK_CONDITION, condition.key) }} />
						</S.WarningIcon>
						<S.FactorText>
							<WorkConditionLabel condition={condition} />
						</S.FactorText>
					</S.FactorItem>
				))}
			</S.FactorsList>
		</S.AccordionContent>
	);
}
